/**
 * Settings Management Service
 * Part of TASK-334: Frontend Admin Panel
 *
 * Handles account settings and security settings management
 * for the admin panel.
 */

import { httpClient } from '../../lib/httpClient';
import type {
  AccountSettings,
  AccountSettingsUpdateData,
  SecuritySettings,
  SecuritySettingsUpdateData,
  VisibilityModesResponse,
} from '../../types/admin';

class SettingsService {
  private readonly basePath = '/api/admin/settings';

  // ==================== Account Settings ====================

  /**
   * Get account settings
   */
  async getAccountSettings(): Promise<AccountSettings> {
    try {
      const data = await httpClient.get<AccountSettings>(`${this.basePath}/account`);
      return data;
    } catch (error) {
      console.error('Error fetching account settings:', error);
      throw error;
    }
  }

  /**
   * Update account settings
   */
  async updateAccountSettings(updates: AccountSettingsUpdateData): Promise<AccountSettings> {
    try {
      const data = await httpClient.put<AccountSettings>(`${this.basePath}/account`, updates);
      return data;
    } catch (error) {
      console.error('Error updating account settings:', error);
      throw error;
    }
  }

  /**
   * Get available visibility mode options
   */
  async getVisibilityModes(): Promise<VisibilityModesResponse> {
    try {
      const data = await httpClient.get<VisibilityModesResponse>(
        `${this.basePath}/visibility-modes`
      );
      return data;
    } catch (error) {
      console.error('Error fetching visibility modes:', error);
      throw error;
    }
  }

  /**
   * Get sensitive data role options
   */
  async getSensitiveDataRoles(): Promise<{
    roles: { role: string; label: string; description: string }[];
    current_roles: string[];
  }> {
    try {
      const data = await httpClient.get<{
        roles: { role: string; label: string; description: string }[];
        current_roles: string[];
      }>(`${this.basePath}/sensitive-data-roles`);
      return data;
    } catch (error) {
      console.error('Error fetching sensitive data roles:', error);
      throw error;
    }
  }

  // ==================== Security Settings ====================

  /**
   * Get security settings
   */
  async getSecuritySettings(): Promise<SecuritySettings> {
    try {
      const data = await httpClient.get<SecuritySettings>(`${this.basePath}/security`);
      return data;
    } catch (error) {
      console.error('Error fetching security settings:', error);
      throw error;
    }
  }

  /**
   * Update security settings
   */
  async updateSecuritySettings(updates: SecuritySettingsUpdateData): Promise<SecuritySettings> {
    try {
      const data = await httpClient.put<SecuritySettings>(`${this.basePath}/security`, updates);
      return data;
    } catch (error) {
      console.error('Error updating security settings:', error);
      throw error;
    }
  }

  /**
   * Reset security settings to Swiss banking defaults
   */
  async resetSecuritySettingsToDefaults(): Promise<SecuritySettings> {
    try {
      const data = await httpClient.post<SecuritySettings>(
        `${this.basePath}/security/reset-to-defaults`
      );
      return data;
    } catch (error) {
      console.error('Error resetting security settings:', error);
      throw error;
    }
  }

  // ==================== Validation Helpers ====================

  /**
   * Validate password against policy
   */
  validatePassword(
    password: string,
    settings: SecuritySettings
  ): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (password.length < settings.min_password_length) {
      errors.push(`Password must be at least ${settings.min_password_length} characters`);
    }

    if (settings.require_uppercase && !/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }

    if (settings.require_lowercase && !/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }

    if (settings.require_numbers && !/[0-9]/.test(password)) {
      errors.push('Password must contain at least one number');
    }

    if (settings.require_special_chars && !/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
      errors.push('Password must contain at least one special character');
    }

    return { valid: errors.length === 0, errors };
  }

  /**
   * Get password policy description
   */
  getPasswordPolicyDescription(settings: SecuritySettings): string[] {
    const requirements: string[] = [];

    requirements.push(`At least ${settings.min_password_length} characters`);

    if (settings.require_uppercase) {
      requirements.push('At least one uppercase letter');
    }

    if (settings.require_lowercase) {
      requirements.push('At least one lowercase letter');
    }

    if (settings.require_numbers) {
      requirements.push('At least one number');
    }

    if (settings.require_special_chars) {
      requirements.push('At least one special character');
    }

    if (settings.password_expiry_days) {
      requirements.push(`Password expires after ${settings.password_expiry_days} days`);
    }

    return requirements;
  }

  /**
   * Format session timeout for display
   */
  formatSessionTimeout(minutes: number): string {
    if (minutes < 60) {
      return `${minutes} minutes`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    }
    return `${hours} hour${hours > 1 ? 's' : ''} ${remainingMinutes} minutes`;
  }

  /**
   * Format lockout duration for display
   */
  formatLockoutDuration(minutes: number): string {
    if (minutes < 60) {
      return `${minutes} minute${minutes > 1 ? 's' : ''}`;
    }
    const hours = Math.floor(minutes / 60);
    return `${hours} hour${hours > 1 ? 's' : ''}`;
  }
}

export const settingsService = new SettingsService();
